import { ApplicationRef, Inject, Injectable } from '@angular/core';
import { AsyncSubject, Observable, ReplaySubject, zip, take } from 'rxjs';

import { SocialUser, LoginProvider } from '@personal-area/global/social-authorization/models';

/**
 * An interface to define the shape of the service configuration options.
 */
export interface SocialAuthServiceConfig {
  autoLogin?: boolean;
  providers: { id: string; provider: LoginProvider }[];
  onError?: (error: any) => any;
}

/**
 * The service encapsulating the social login functionality. Exposes methods like
 * `signIn`, `signOut`. Also, exposes an `authState` `Observable` that one can
 * subscribe to get the current logged in user information.
 *
 * @dynamic
 */
@Injectable({ providedIn: 'root' })
export class SocialAuthService {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  static readonly ERR_LOGIN_PROVIDER_NOT_FOUND =
    'Login provider not found';
  // eslint-disable-next-line @typescript-eslint/naming-convention
  static readonly ERR_NOT_LOGGED_IN = 'Not logged in';
  // eslint-disable-next-line @typescript-eslint/naming-convention
  static readonly ERR_NOT_INITIALIZED =
    'Login providers not ready yet. Are there errors on your console?';
  // eslint-disable-next-line @typescript-eslint/naming-convention
  static readonly ERR_NOT_SUPPORTED_FOR_REFRESH_TOKEN =
    'Chosen login provider is not supported for refreshing a token';


  private _providers: Map<string, LoginProvider> = new Map();

  private _user: SocialUser = null;
  private _authState: ReplaySubject<SocialUser> = new ReplaySubject(1);

  /* Consider making this an enum comprising LOADING, LOADED, FAILED etc. */
  private _initialized = false;
  private _initState: AsyncSubject<boolean> = new AsyncSubject();

  /** An `Observable` that one can subscribe to get the current logged in user information */
  get authState(): Observable<SocialUser> {
    return this._authState.asObservable();
  }

  /** An `Observable` to communicate the readiness of the service and associated login providers */
  get initState(): Observable<boolean> {
    return this._initState.asObservable();
  }

  /**
   * @param config A `SocialAuthServiceConfig` object or a `Promise` that resolves to a `SocialAuthServiceConfig` object
   */
  constructor(
    @Inject('SocialAuthServiceConfig')
    config: SocialAuthServiceConfig | Promise<SocialAuthServiceConfig>,
    private _appRef: ApplicationRef
  ) {
    if (config instanceof Promise) {
      config.then((config) => {
        this._initialize(config);
      });
    } else {
      this._initialize(config);
    }
  }

  private _initialize(config: SocialAuthServiceConfig): void {
    const { onError = console.error } = config;

    config.providers.forEach((item) => {
      this._providers.set(item.id, item.provider);
    });

    zip(
      ...Array.from(this._providers.values()).map((provider) =>
        provider.initialize()
      )
    ).pipe(take(1))
      .subscribe({
        error: error => {
          this._initialized = true;
          this._initState.next(this._initialized);
          this._initState.complete();
          onError(error);
        },
        complete: () => {
          this._initialized = true;
          this._initState.next(this._initialized);
          this._initState.complete();
        }
      })
  }

  /**
   * A method used to sign in a user with a specific `LoginProvider`.
   *
   * @param providerId Id with which the `LoginProvider` has been registered with the service
   * @param signInOptions Optional `LoginProvider` specific arguments
   * @returns A `Observable` that resolves to the authenticated user information
   */
  public signIn(providerId: string, signInOptions?: any): Observable<SocialUser> {
    return new Observable<SocialUser>(observer => {
      if (!this._initialized) {
        observer.error(SocialAuthService.ERR_NOT_INITIALIZED);
      } else {
        const providerObject = this._providers.get(providerId);
        if (providerObject) {

          providerObject
            .signIn(signInOptions)
            .subscribe((user: SocialUser) => {
              user.provider = providerId;
              observer.next(user);
              observer.complete();

              this._user = user;
              this._authState.next(user);
              // Say to angular that stream is done, cause sdk code is envoke outside zone
              this._appRef.tick();
            }, error => {
              observer.error(error);
            });
        } else {
          observer.error(SocialAuthService.ERR_LOGIN_PROVIDER_NOT_FOUND);
        }
      }
    });
  }
}
